import React, { useState, useEffect, useRef } from 'react';
import { MessageCircle, Send, Edit3, Trash2, ArrowLeft, Search, MoreVertical, AlertTriangle, X, CheckSquare, Square, Copy, User, Bell, Ban, Check, Flag, MapPin, Calendar, Heart, Sparkles, Target, Wine, Cigarette, Cross, Shield } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useLocation } from 'react-router-dom';
import ReportModal from '../components/ReportModal';
import DatePlanningAssistant from '../components/DatePlanningAssistant';
import Avatar from '../components/Avatar';
import { getDisplayName } from '../utils/displayName';
import { resolveImageUrl } from '../utils/imageUrl';

const Messages = () => {
  const [conversations, setConversations] = useState([]);
  const [activeConversation, setActiveConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [editingMessage, setEditingMessage] = useState(null);
  const [editContent, setEditContent] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [messageToDelete, setMessageToDelete] = useState(null);
  const [selectedMessages, setSelectedMessages] = useState([]);
  const [showBulkDeleteModal, setShowBulkDeleteModal] = useState(false);
  const [swipedMessage, setSwipedMessage] = useState(null);
  const [selectionMode, setSelectionMode] = useState(false);
  const [touchStart, setTouchStart] = useState(null);
  const messagesEndRef = useRef(null);
  const location = useLocation();
  const [openMenuConversationId, setOpenMenuConversationId] = useState(null);
  const [deletingConversation, setDeletingConversation] = useState(null);
  const [blockingUser, setBlockingUser] = useState(null);
  const [showActionMenu, setShowActionMenu] = useState(false);
  const [reportingUser, setReportingUser] = useState(null);
  const [viewingProfile, setViewingProfile] = useState(null);
  const [profileLoading, setProfileLoading] = useState(false);
  const [showDatePlanner, setShowDatePlanner] = useState(false);

  useEffect(() => {
    fetchConversations();
  }, []);

  useEffect(() => {
    // Check if we were navigated here to start a specific conversation
    if (location.state?.startConversationWith) {
      const userToMessage = location.state.startConversationWith;
      setActiveConversation(userToMessage);
      fetchMessages(userToMessage.id);
    }
  }, [location.state]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
  };

  const fetchConversations = async () => {
    try {
      const response = await axios.get('/conversations');
      setConversations(response.data);
    } catch (error) {
      console.error('Error fetching conversations:', error);
      toast.error('Failed to load conversations');
    } finally {
      setLoading(false);
    }
  };

  const fetchUserProfile = async (userId) => {
    setProfileLoading(true);
    try {
      const response = await axios.get(`/users/${userId}`);
      setViewingProfile(response.data);
    } catch (error) {
      console.error('Error fetching user profile:', error);
      toast.error('Failed to load profile');
    } finally {
      setProfileLoading(false);
    }
  };

  const fetchMessages = async (userId) => {
    try {
      const response = await axios.get(`/messages/${userId}`);
      setMessages(response.data);
      
      // Find the conversation user info
      const conversation = conversations.find(c => c.user.id === userId);
      setActiveConversation(conversation?.user || { id: userId, full_name: 'User', username: 'user' });
    } catch (error) {
      console.error('Error fetching messages:', error);
      toast.error('Failed to load messages');
    }
  };

  const handleDeleteConversation = async () => {
    try {
      await axios.delete(`/conversations/${deletingConversation.user.id}`);
      toast.success('Conversation deleted');
      setDeletingConversation(null);
      if (activeConversation?.id === deletingConversation.user.id) {
        setActiveConversation(null);
      }
      fetchConversations();
    } catch (error) {
      console.error('Error deleting conversation:', error);
      toast.error('Failed to delete conversation');
    }
  };

  const handleBlockUser = async () => {
    try {
      await axios.post(`/users/${blockingUser.user.id}/block`);
      toast.success('User blocked successfully');
      setBlockingUser(null);
      if (activeConversation?.id === blockingUser.user.id) {
        setActiveConversation(null);
      }
      fetchConversations();
    } catch (error) {
      console.error('Error blocking user:', error);
      toast.error('Failed to block user');
    }
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !activeConversation) return;

    try {
      const response = await axios.post('/messages/send', {
        receiver_id: activeConversation.id,
        content: newMessage.trim(),
        message_type: 'text'
      });

      setMessages(prev => [...prev, response.data]);
      setNewMessage('');
      fetchConversations(); // Refresh conversations list
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error(error.response?.data?.detail || 'Failed to send message');
    }
  };

  const editMessage = async (messageId) => {
    if (!editContent.trim()) {
      toast.error('Message cannot be empty');
      return;
    }

    try {
      const response = await axios.put(`/messages/${messageId}`, {
        content: editContent.trim()
      });

      setMessages(prev => prev.map(msg => 
        msg.id === messageId ? response.data : msg
      ));
      setEditingMessage(null);
      setEditContent('');
      toast.success('Message updated');
    } catch (error) {
      console.error('Error editing message:', error);
      toast.error('Failed to edit message');
    }
  };

  const handleDeleteClick = (messageId) => {
    setMessageToDelete(messageId);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!messageToDelete) return;

    try {
      await axios.delete(`/messages/${messageToDelete}`);
      setMessages(prev => prev.filter(msg => msg.id !== messageToDelete));
      toast.success('Message deleted');
      setShowDeleteModal(false);
      setMessageToDelete(null);
    } catch (error) {
      console.error('Error deleting message:', error);
      toast.error('Failed to delete message');
      setShowDeleteModal(false);
      setMessageToDelete(null);
    }
  };

  const cancelDelete = () => {
    setShowDeleteModal(false);
    setMessageToDelete(null);
  };

  const startEdit = (message) => {
    setEditingMessage(message.id);
    setEditContent(message.content);
  };

  const cancelEdit = () => {
    setEditingMessage(null);
    setEditContent('');
  };

  const toggleMessageSelection = (messageId) => {
    setSelectedMessages(prev => 
      prev.includes(messageId) 
        ? prev.filter(id => id !== messageId)
        : [...prev, messageId]
    );
  };

  const selectAllOwnMessages = () => {
    const userId = JSON.parse(localStorage.getItem('userData'))?.id;
    const ownMessageIds = messages
      .filter(msg => msg.sender_id === userId)
      .map(msg => msg.id);
    
    if (selectedMessages.length === ownMessageIds.length) {
      // Deselect all
      setSelectedMessages([]);
    } else {
      // Select all
      setSelectedMessages(ownMessageIds);
    }
  };

  const handleBulkDelete = () => {
    if (selectedMessages.length === 0) {
      toast.error('No messages selected');
      return;
    }
    setShowBulkDeleteModal(true);
  };

  const confirmBulkDelete = async () => {
    if (selectedMessages.length === 0) return;

    try {
      // Delete all selected messages
      await Promise.all(
        selectedMessages.map(messageId => 
          axios.delete(`/messages/${messageId}`)
        )
      );

      setMessages(prev => prev.filter(msg => !selectedMessages.includes(msg.id)));
      toast.success(`${selectedMessages.length} message${selectedMessages.length > 1 ? 's' : ''} deleted`);
      setSelectedMessages([]);
      setShowBulkDeleteModal(false);
      fetchConversations(); // Refresh conversations list
    } catch (error) {
      console.error('Error deleting messages:', error);
      toast.error('Failed to delete some messages');
      setShowBulkDeleteModal(false);
    }
  };

  const cancelBulkDelete = () => {
    setShowBulkDeleteModal(false);
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInHours = (now - date) / (1000 * 60 * 60);
    
    if (diffInHours < 24) {
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } else {
      return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
    }
  };

  const isWithin12Hours = (dateString) => {
    const messageDate = new Date(dateString);
    const now = new Date();
    const diffInHours = (now - messageDate) / (1000 * 60 * 60);
    return diffInHours <= 12;
  };

  const isLastSentMessage = (messageId) => {
    const userId = JSON.parse(localStorage.getItem('userData'))?.id;
    const userMessages = messages.filter(msg => msg.sender_id === userId);
    if (userMessages.length === 0) return false;
    const lastMessage = userMessages[userMessages.length - 1];
    return lastMessage.id === messageId;
  };

  const canEditMessage = (message) => {
    const userId = JSON.parse(localStorage.getItem('userData'))?.id;
    return message.sender_id === userId && 
           isLastSentMessage(message.id) && 
           isWithin12Hours(message.created_at);
  };

  const toggleSelectionMode = () => {
    setSelectionMode(!selectionMode);
    setSelectedMessages([]);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-green-50 to-teal-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600 mx-auto mb-4"></div>
          <p className="text-emerald-700 font-medium">Loading messages...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-green-50 to-teal-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white/80 backdrop-blur-sm rounded-3xl shadow-xl overflow-hidden">
          <div className="flex h-[700px]">
            
            {/* Conversations Sidebar */}
            <div className={`w-full md:w-1/3 border-r border-emerald-200/50 ${activeConversation ? 'hidden md:block' : ''}`}>
              <div className="p-6 border-b border-emerald-200/50">
                <h1 className="text-2xl font-bold text-emerald-800 mb-2">Messages</h1>
                <div className="relative">
                  <Search className="absolute left-3 top-3 w-4 h-4 text-emerald-400" />
                  <input
                    type="text"
                    placeholder="Search conversations..."
                    className="w-full pl-10 pr-4 py-2 border border-emerald-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                  />
                </div>
              </div>
              
              <div className="overflow-y-auto h-full">
                {conversations.length === 0 ? (
                  <div className="p-6 text-center">
                    <MessageCircle className="w-16 h-16 text-emerald-300 mx-auto mb-4" />
                    <h3 className="text-lg font-semibold text-emerald-800 mb-2">No conversations yet</h3>
                    <p className="text-emerald-600">Start messaging people you've liked or matched with!</p>
                  </div>
                ) : (
                  conversations.map((conversation) => (
                    <div
                      key={conversation.user.id}
                      data-testid={`conversation-item-${conversation.user.id}`}
                      className={`p-4 border-b border-emerald-100/50 hover:bg-emerald-50/50 transition-colors ${
                        activeConversation?.id === conversation.user.id ? 'bg-emerald-100/50' : ''
                      }`}
                    >
                      {/* Header with Avatar, Name, and Three-Dot Menu */}
                      <div className="flex items-start justify-between mb-2">
                        <div 
                          className="flex items-center space-x-3 flex-1 cursor-pointer"
                          onClick={() => fetchMessages(conversation.user.id)}
                        >
                          <Avatar
                            src={conversation.user.profile_image}
                            name={conversation.user.full_name}
                            sizeClass="w-12 h-12"
                            textClass="text-base"
                            testId={`conversation-avatar-${conversation.user.id}`}
                          />
                          <div className="flex-1 min-w-0">
                            <h3 className="font-semibold text-emerald-800 truncate">
                              {getDisplayName(conversation.user.full_name)}
                            </h3>
                            <p className="text-xs text-emerald-500">
                              {formatTime(conversation.last_message.created_at)}
                            </p>
                          </div>
                        </div>

                        {/* Three-Dot Menu - Right Side */}
                        <div className="flex items-center space-x-2">
                          {conversation.unread_count > 0 && (
                            <span className="inline-flex items-center justify-center w-5 h-5 bg-emerald-600 text-white text-xs rounded-full">
                              {conversation.unread_count}
                            </span>
                          )}
                          
                          <div className="relative">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setOpenMenuConversationId(
                                  openMenuConversationId === conversation.user.id ? null : conversation.user.id
                                );
                              }}
                              className="p-1 hover:bg-emerald-100 rounded-lg transition-colors"
                              data-testid={`conversation-menu-btn-${conversation.user.id}`}
                            >
                              <MoreVertical className="w-5 h-5 text-emerald-600" />
                            </button>
                            
                            {openMenuConversationId === conversation.user.id && (
                              <>
                                <div 
                                  className="fixed inset-0 z-10" 
                                  onClick={() => setOpenMenuConversationId(null)}
                                />
                                
                                <div className="absolute right-0 mt-2 w-52 bg-white border border-emerald-200 rounded-lg shadow-xl z-20">
                                  {/* View Profile */}
                                  <button
                                    onClick={() => {
                                      fetchUserProfile(conversation.user.id);
                                      setOpenMenuConversationId(null);
                                    }}
                                    className="w-full px-4 py-3 text-left hover:bg-emerald-50 flex items-center space-x-3 transition-colors"
                                  >
                                    <User className="w-4 h-4 text-emerald-600" />
                                    <span className="text-emerald-800 font-medium">View Profile</span>
                                  </button>

                                  {/* Mute Conversation */}
                                  <button
                                    onClick={() => {
                                      toast.success('Conversation muted');
                                      setOpenMenuConversationId(null);
                                    }}
                                    className="group relative overflow-hidden w-full px-4 py-3 text-left flex items-center space-x-3 transition-all bg-gradient-to-r from-blue-500 to-indigo-500 hover:from-blue-600 hover:to-indigo-600 border-t border-white/20"
                                  >
                                    <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center">
                                      <Bell className="w-4 h-4 text-white" />
                                    </div>
                                    <span className="text-white font-medium">Mute Notifications</span>
                                  </button>

                                  {/* Delete Conversation */}
                                  <button
                                    onClick={() => {
                                      setDeletingConversation(conversation);
                                      setOpenMenuConversationId(null);
                                    }}
                                    className="group relative overflow-hidden w-full px-4 py-3 text-left flex items-center space-x-3 transition-all bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 border-t border-white/20"
                                  >
                                    <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center">
                                      <Trash2 className="w-4 h-4 text-white" />
                                    </div>
                                    <span className="text-white font-medium">Delete Conversation</span>
                                  </button>

                                  {/* Block User */}
                                  <button
                                    onClick={() => {
                                      setBlockingUser(conversation);
                                      setOpenMenuConversationId(null);
                                    }}
                                    className="group relative overflow-hidden w-full px-4 py-3 text-left flex items-center space-x-3 transition-all bg-gradient-to-r from-red-500 to-rose-500 hover:from-red-600 hover:to-rose-600 border-t border-white/20"
                                  >
                                    <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center">
                                      <Ban className="w-4 h-4 text-white" />
                                    </div>
                                    <span className="text-white font-medium">Block User</span>
                                  </button>

                                  {/* Report User */}
                                  <button
                                    onClick={() => {
                                      setReportingUser({
                                        id: conversation.user.id,
                                        name: getDisplayName(conversation.user.full_name)
                                      });
                                      setOpenMenuConversationId(null);
                                    }}
                                    className="group relative overflow-hidden w-full px-4 py-3 text-left flex items-center space-x-3 transition-all bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 border-t border-white/20 rounded-b-lg"
                                  >
                                    <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center">
                                      <Flag className="w-4 h-4 text-white" />
                                    </div>
                                    <span className="text-white font-medium">Report User</span>
                                  </button>
                                </div>
                              </>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Last Message Preview */}
                      <div 
                        className="ml-15 cursor-pointer"
                        onClick={() => fetchMessages(conversation.user.id)}
                      >
                        <p className="text-sm text-emerald-600 truncate">
                          {conversation.last_message.content}
                        </p>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* Chat Area */}
            <div className={`flex-1 flex flex-col ${!activeConversation ? 'hidden md:flex' : ''}`}>
              {activeConversation ? (
                <>
                  {/* Chat Header */}
                  <div className="p-6 border-b border-emerald-200/50 bg-gradient-to-r from-emerald-50 to-green-50">
                    <div className="flex items-center space-x-4">
                      <button
                        onClick={() => setActiveConversation(null)}
                        className="md:hidden text-emerald-600 hover:text-emerald-800"
                      >
                        <ArrowLeft className="w-6 h-6" />
                      </button>
                      <Avatar
                        src={activeConversation.profile_image}
                        name={activeConversation.full_name}
                        sizeClass="w-12 h-12"
                        textClass="text-base"
                        testId="chat-header-avatar"
                      />
                      <div>
                        <h2 className="text-xl font-bold text-emerald-800">{getDisplayName(activeConversation.full_name)}</h2>
                        <p className="text-emerald-600">@{activeConversation.username}</p>
                      </div>
                    </div>
                  </div>

                  {/* Enhanced Action Bar with Three-Dot Menu on Right */}
                  {messages.length > 0 && messages.some(msg => msg.sender_id === (JSON.parse(localStorage.getItem('userData'))?.id)) && (
                    <div className="p-4 border-b border-emerald-200/50 bg-gradient-to-r from-emerald-50 via-green-50 to-emerald-50">
                      <div className="flex items-center justify-between">
                        {/* Left side - Selection info */}
                        <div className="flex items-center space-x-3">
                          {selectionMode && (
                            <>
                              {selectedMessages.length > 0 && (
                                <div className="flex items-center space-x-2 px-3 py-2 bg-emerald-100 rounded-lg border border-emerald-300">
                                  <span className="text-sm font-semibold text-emerald-700">
                                    {selectedMessages.length} selected
                                  </span>
                                </div>
                              )}
                            </>
                          )}
                          
                          {!selectionMode && (
                            <div className="flex items-center space-x-2 bg-white/80 backdrop-blur-sm px-3 py-2 rounded-lg border border-emerald-200">
                              <span className="text-lg">💡</span>
                              <p className="text-xs font-medium text-emerald-700">
                                Swipe left on messages for quick actions
                              </p>
                            </div>
                          )}
                        </div>
                        
                        {/* Right side - Three-Dot Menu */}
                        <div className="relative">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setShowActionMenu(!showActionMenu);
                            }}
                            className="p-2 hover:bg-emerald-100 rounded-lg transition-colors border border-emerald-200 bg-white"
                          >
                            <MoreVertical className="w-5 h-5 text-emerald-600" />
                          </button>
                          
                          {showActionMenu && (
                            <>
                              <div 
                                className="fixed inset-0 z-10" 
                                onClick={() => setShowActionMenu(false)}
                              />
                              
                              <div className="absolute right-0 mt-2 w-56 rounded-xl shadow-2xl z-20 overflow-hidden">
                                {!selectionMode ? (
                                  <>
                                    {/* Enter Selection Mode */}
                                    <button
                                      onClick={() => {
                                        toggleSelectionMode();
                                        setShowActionMenu(false);
                                      }}
                                      className="w-full px-4 py-3 text-left flex items-center space-x-3 transition-all bg-gradient-to-r from-emerald-500 to-green-500 hover:from-emerald-600 hover:to-green-600 rounded-t-xl"
                                    >
                                      <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center">
                                        <CheckSquare className="w-4 h-4 text-white" />
                                      </div>
                                      <span className="text-white font-medium">Select Messages</span>
                                    </button>
                                    
                                    {/* Plan a Date */}
                                    <button
                                      onClick={() => {
                                        setShowDatePlanner(true);
                                        setShowActionMenu(false);
                                      }}
                                      className="w-full px-4 py-3 text-left flex items-center space-x-3 transition-all bg-gradient-to-r from-pink-500 to-rose-500 hover:from-pink-600 hover:to-rose-600 border-t border-white/20 rounded-b-xl"
                                      data-testid="plan-date-btn"
                                    >
                                      <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center">
                                        <Heart className="w-4 h-4 text-white" />
                                      </div>
                                      <span className="text-white font-medium">Plan a Date</span>
                                    </button>
                                  </>
                                ) : (
                                  <>
                                    {/* Select All */}
                                    <button
                                      onClick={() => {
                                        selectAllOwnMessages();
                                        setShowActionMenu(false);
                                      }}
                                      className="w-full px-4 py-3 text-left flex items-center space-x-3 transition-all bg-gradient-to-r from-emerald-500 to-green-500 hover:from-emerald-600 hover:to-green-600 rounded-t-xl"
                                    >
                                      <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center">
                                        <CheckSquare className="w-4 h-4 text-white" />
                                      </div>
                                      <span className="text-white font-medium">
                                        {selectedMessages.length === messages.filter(msg => msg.sender_id === (JSON.parse(localStorage.getItem('userData'))?.id)).length && selectedMessages.length > 0 ? 'Deselect All' : 'Select All'}
                                      </span>
                                    </button>
                                    
                                    {/* Delete Selected */}
                                    {selectedMessages.length > 0 && (
                                      <button
                                        onClick={() => {
                                          handleBulkDelete();
                                          setShowActionMenu(false);
                                        }}
                                        className="w-full px-4 py-3 text-left flex items-center space-x-3 transition-all bg-gradient-to-r from-red-500 to-rose-500 hover:from-red-600 hover:to-rose-600 border-t border-white/20"
                                      >
                                        <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center">
                                          <Trash2 className="w-4 h-4 text-white" />
                                        </div>
                                        <span className="text-white font-medium">Delete Selected ({selectedMessages.length})</span>
                                      </button>
                                    )}
                                    
                                    {/* Exit Selection Mode */}
                                    <button
                                      onClick={() => {
                                        toggleSelectionMode();
                                        setShowActionMenu(false);
                                      }}
                                      className="w-full px-4 py-3 text-left flex items-center space-x-3 transition-all bg-gradient-to-r from-blue-500 to-indigo-500 hover:from-blue-600 hover:to-indigo-600 border-t border-white/20 rounded-b-xl"
                                    >
                                      <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center">
                                        <X className="w-4 h-4 text-white" />
                                      </div>
                                      <span className="text-white font-medium">Exit Selection Mode</span>
                                    </button>
                                  </>
                                )}
                              </div>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Messages */}
                  <div className="flex-1 overflow-y-auto p-6 space-y-4">
                    {messages.map((message) => {
                      const isOwn = message.sender_id === (JSON.parse(localStorage.getItem('userData'))?.id);
                      const isSelected = selectedMessages.includes(message.id);
                      const isSwiped = swipedMessage === message.id;
                      const canEdit = canEditMessage(message);
                      
                      return (
                        <div 
                          key={message.id} 
                          className={`flex items-center gap-2 ${isOwn ? 'justify-end' : 'justify-start'} relative`}
                          onTouchStart={(e) => {
                            if (!isOwn || selectionMode) return;
                            const touch = e.touches[0];
                            setTouchStart({ x: touch.clientX, messageId: message.id });
                          }}
                          onTouchMove={(e) => {
                            if (!isOwn || editingMessage === message.id || !touchStart || touchStart.messageId !== message.id) return;
                            const touch = e.touches[0];
                            const deltaX = touch.clientX - touchStart.x;
                            if (deltaX < -50) {
                              setSwipedMessage(message.id);
                              setTouchStart(null);
                            }
                          }}
                          onTouchEnd={() => {
                            setTouchStart(null);
                          }}
                          onMouseDown={(e) => {
                            if (!isOwn || selectionMode) return;
                            setTouchStart({ x: e.clientX, messageId: message.id });
                          }}
                          onMouseMove={(e) => {
                            if (!isOwn || editingMessage === message.id || !touchStart || touchStart.messageId !== message.id) return;
                            const deltaX = e.clientX - touchStart.x;
                            if (deltaX < -50) {
                              setSwipedMessage(message.id);
                              setTouchStart(null);
                            }
                          }}
                          onMouseUp={() => {
                            setTouchStart(null);
                          }}
                          onMouseLeave={() => {
                            setTouchStart(null);
                          }}
                        >
                          {/* Checkbox for own messages (only in selection mode) */}
                          {isOwn && selectionMode && (
                            <button
                              onClick={() => toggleMessageSelection(message.id)}
                              className="flex-shrink-0"
                            >
                              {isSelected ? (
                                <CheckSquare className="w-5 h-5 text-emerald-600" />
                              ) : (
                                <Square className="w-5 h-5 text-emerald-400 hover:text-emerald-600" />
                              )}
                            </button>
                          )}

                          <div className={`max-w-xs lg:max-w-md ${isOwn ? '' : 'order-1'} transition-transform duration-200 ${isSwiped ? '-translate-x-16' : ''}`}>
                            <div
                              className={`p-3 rounded-2xl ${
                                isOwn
                                  ? 'bg-gradient-to-r from-emerald-500 to-green-600 text-white'
                                  : 'bg-white border border-emerald-200 text-emerald-800'
                              } ${isSelected ? 'ring-2 ring-emerald-500' : ''}`}
                            >
                              {editingMessage === message.id ? (
                                <div className="space-y-2">
                                  <textarea
                                    value={editContent}
                                    onChange={(e) => setEditContent(e.target.value)}
                                    className="w-full p-2 border border-emerald-200 rounded-lg resize-none text-emerald-800"
                                    rows="2"
                                    autoFocus
                                  />
                                  <div className="flex space-x-2">
                                    <button
                                      onClick={() => editMessage(message.id)}
                                      className="flex items-center space-x-1 px-4 py-2 bg-gradient-to-r from-emerald-600 to-emerald-700 text-white text-sm rounded-lg hover:from-emerald-700 hover:to-emerald-800 shadow-md hover:shadow-lg transition-all"
                                    >
                                      <Check className="w-4 h-4" />
                                      <span>Save</span>
                                    </button>
                                    <button
                                      onClick={cancelEdit}
                                      className="flex items-center space-x-1 px-4 py-2 bg-gray-400 text-white text-sm rounded-lg hover:bg-gray-500 shadow-md transition-all"
                                    >
                                      <X className="w-4 h-4" />
                                      <span>Cancel</span>
                                    </button>
                                  </div>
                                </div>
                              ) : (
                                <>
                                  <p className="text-sm">{message.content}</p>
                                  {message.is_edited && (
                                    <p className={`text-xs mt-1 ${isOwn ? 'text-emerald-100' : 'text-emerald-500'}`}>
                                      (edited)
                                    </p>
                                  )}
                                </>
                              )}
                            </div>
                            
                            <div className={`flex items-center gap-2 mt-1 ${isOwn ? 'justify-end' : 'justify-start'}`}>
                              <p className={`text-xs ${isOwn ? 'text-emerald-600' : 'text-emerald-500'}`}>
                                {formatTime(message.created_at)}
                              </p>
                              {canEdit && (
                                <span className="inline-flex items-center px-2 py-0.5 bg-emerald-100 text-emerald-700 rounded-full text-xs font-medium animate-pulse" title="Editable (last message, within 12 hours)">
                                  <Edit3 className="w-3 h-3 mr-1" />
                                  Editable
                                </span>
                              )}
                            </div>
                          </div>

                          {/* Tap anywhere outside swiped message to close */}
                          {isSwiped && (
                            <div 
                              className="fixed inset-0 z-0"
                              onClick={() => setSwipedMessage(null)}
                            />
                          )}
                          
                          {/* Edit/Delete buttons (shown on swipe) */}
                          {isOwn && editingMessage !== message.id && isSwiped && !selectionMode && (
                            <div className="flex flex-col space-y-1 flex-shrink-0 transition-all duration-200 relative z-50">
                              <button
                                onClick={() => {
                                  startEdit(message);
                                  setSwipedMessage(null);
                                }}
                                className="p-2 bg-emerald-500 text-white rounded-full shadow-lg hover:bg-emerald-600 transition-colors"
                                title="Edit message"
                              >
                                <Edit3 className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => {
                                  handleDeleteClick(message.id);
                                  setSwipedMessage(null);
                                }}
                                className="p-2 bg-red-500 text-white rounded-full shadow-lg hover:bg-red-600 transition-colors"
                                title="Delete message"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          )}
                        </div>
                      );
                    })}
                    <div ref={messagesEndRef} />
                  </div>

                  {/* Message Input */}
                  <form onSubmit={sendMessage} className="p-6 border-t border-emerald-200/50 bg-gradient-to-r from-emerald-50 to-green-50">
                    <div className="flex space-x-4">
                      <input
                        type="text"
                        value={newMessage}
                        onChange={(e) => setNewMessage(e.target.value)}
                        placeholder="Type your message..."
                        className="flex-1 p-3 border border-emerald-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                      />
                      <button
                        type="submit"
                        disabled={!newMessage.trim()}
                        className="p-3 bg-gradient-to-r from-emerald-500 to-green-600 text-white rounded-xl hover:from-emerald-600 hover:to-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                      >
                        <Send className="w-5 h-5" />
                      </button>
                    </div>
                  </form>
                </>
              ) : (
                <div className="flex-1 flex items-center justify-center">
                  <div className="text-center">
                    <MessageCircle className="w-24 h-24 text-emerald-300 mx-auto mb-6" />
                    <h3 className="text-2xl font-bold text-emerald-800 mb-2">Select a conversation</h3>
                    <p className="text-emerald-600">Choose someone from your conversations to start messaging</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Delete Confirmation Modal */}
        {showDeleteModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white dark:bg-slate-800 rounded-2xl p-6 max-w-md w-full mx-4 shadow-2xl">
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-10 h-10 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center">
                  <AlertTriangle className="w-5 h-5 text-red-600 dark:text-red-400" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Delete Message</h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400">This action cannot be undone</p>
                </div>
                <button
                  onClick={cancelDelete}
                  className="ml-auto p-1 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-full transition-colors"
                >
                  <X className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                </button>
              </div>

              <div className="mb-6">
                <p className="text-gray-700 dark:text-gray-300">
                  Are you sure you want to delete this message? This will permanently remove it from the conversation and cannot be undone.
                </p>
              </div>

              <div className="flex space-x-3">
                <button
                  onClick={cancelDelete}
                  className="flex-1 px-4 py-2 border border-gray-300 dark:border-slate-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmDelete}
                  className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                  data-testid="confirm-delete-message-btn"
                >
                  Delete Message
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Bulk Delete Confirmation Modal */}
        {showBulkDeleteModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white dark:bg-slate-800 rounded-2xl p-6 max-w-md w-full mx-4 shadow-2xl">
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-10 h-10 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center">
                  <AlertTriangle className="w-5 h-5 text-red-600 dark:text-red-400" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Delete Multiple Messages</h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400">This action cannot be undone</p>
                </div>
                <button
                  onClick={cancelBulkDelete}
                  className="ml-auto p-1 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-full transition-colors"
                >
                  <X className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                </button>
              </div>

              <div className="mb-6">
                <p className="text-gray-700 dark:text-gray-300">
                  Are you sure you want to delete <strong>{selectedMessages.length}</strong> message{selectedMessages.length > 1 ? 's' : ''}? 
                  This will permanently remove {selectedMessages.length > 1 ? 'them' : 'it'} from the conversation and cannot be undone.
                </p>
              </div>

              <div className="flex space-x-3">
                <button
                  onClick={cancelBulkDelete}
                  className="flex-1 px-4 py-2 border border-gray-300 dark:border-slate-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmBulkDelete}
                  className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                  data-testid="confirm-bulk-delete-btn"
                >
                  Delete {selectedMessages.length} Message{selectedMessages.length > 1 ? 's' : ''}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Delete Conversation Modal */}
        {deletingConversation && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-2xl p-6 max-w-sm w-full">
              <h3 className="text-lg font-semibold text-emerald-800 mb-3">Delete Conversation</h3>
              <p className="text-emerald-600 mb-6">
                Are you sure you want to delete your conversation with <strong>{getDisplayName(deletingConversation.user.full_name)}</strong>? 
                All messages will be permanently deleted. This action cannot be undone.
              </p>
              <div className="flex space-x-3">
                <button
                  onClick={() => setDeletingConversation(null)}
                  className="flex-1 btn-secondary"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteConversation}
                  className="flex-1 bg-red-600 hover:bg-red-700 text-white rounded-full px-4 py-2 transition-colors"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Block User Modal */}
        {blockingUser && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-2xl p-6 max-w-sm w-full">
              <h3 className="text-lg font-semibold text-emerald-800 mb-3">Block User</h3>
              <p className="text-emerald-600 mb-6">
                Are you sure you want to block <strong>{getDisplayName(blockingUser.user.full_name)}</strong>? 
                They will no longer be able to message you or see your profile. You can unblock them later from settings.
              </p>
              <div className="flex space-x-3">
                <button
                  onClick={() => setBlockingUser(null)}
                  className="flex-1 btn-secondary"
                >
                  Cancel
                </button>
                <button
                  onClick={handleBlockUser}
                  className="flex-1 bg-red-600 hover:bg-red-700 text-white rounded-full px-4 py-2 transition-colors"
                >
                  Block
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Report User Modal */}
        {reportingUser && (
          <ReportModal
            isOpen={!!reportingUser}
            onClose={() => setReportingUser(null)}
            reportedUserId={reportingUser.id}
            reportedUserName={reportingUser.name}
            reportType="user"
          />
        )}

        {/* View Profile Modal */}
        {viewingProfile && (
          <div 
            className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4"
            onClick={() => setViewingProfile(null)}
          >
            <div 
              className="bg-white dark:bg-slate-800 rounded-2xl max-w-lg w-full max-h-[85vh] overflow-hidden shadow-2xl relative"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Prominent Exit Button */}
              <button
                onClick={() => setViewingProfile(null)}
                className="absolute top-4 right-4 z-10 p-3 bg-white dark:bg-slate-700 hover:bg-gray-100 dark:hover:bg-slate-600 rounded-full shadow-lg transition-all hover:scale-110"
              >
                <X className="w-6 h-6 text-gray-700 dark:text-gray-200" />
              </button>

              {/* Header with Photo */}
              <div className="relative h-48 bg-gradient-to-br from-emerald-100 to-teal-100 dark:from-emerald-800 dark:to-teal-800">
                {viewingProfile.profile_image ? (
                  <img
                    src={resolveImageUrl(viewingProfile.profile_image)}
                    alt={getDisplayName(viewingProfile.full_name)}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <User className="w-20 h-20 text-emerald-400 dark:text-emerald-300" />
                  </div>
                )}
              </div>

              {/* Profile Content */}
              <div className="p-6 overflow-y-auto max-h-[calc(85vh-12rem)]">
                {/* Name and Basic Info */}
                <div className="mb-6">
                  <div className="flex items-center space-x-2">
                    <h2 className="text-2xl font-bold text-emerald-800 dark:text-emerald-100">
                      {getDisplayName(viewingProfile.full_name)}
                    </h2>
                    {viewingProfile.is_admin && (
                      <span className="inline-flex items-center px-2 py-0.5 bg-emerald-500/20 text-emerald-600 dark:text-emerald-400 text-xs font-semibold rounded-full" title="Admin">
                        <Shield className="w-3 h-3 mr-1" />
                        Admin
                      </span>
                    )}
                  </div>
                  <div className="flex flex-wrap items-center gap-4 text-emerald-600 dark:text-emerald-300 mt-2">
                    {viewingProfile.age && (
                      <div className="flex items-center space-x-1">
                        <Calendar className="w-4 h-4" />
                        <span>{viewingProfile.age} years old</span>
                      </div>
                    )}
                    {viewingProfile.location && (
                      <div className="flex items-center space-x-1">
                        <MapPin className="w-4 h-4" />
                        <span>{viewingProfile.location}</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Bio */}
                {viewingProfile.bio && (
                  <div className="mb-6">
                    <h3 className="text-sm font-semibold text-emerald-800 dark:text-emerald-100 mb-2 flex items-center">
                      <MessageCircle className="w-4 h-4 mr-2" />
                      About Me
                    </h3>
                    <p className="text-emerald-700 dark:text-emerald-200 leading-relaxed">
                      {viewingProfile.bio}
                    </p>
                  </div>
                )}

                {/* Mental Health Goals */}
                {viewingProfile.mental_health_goals && viewingProfile.mental_health_goals.length > 0 && (
                  <div className="mb-6">
                    <h3 className="text-sm font-semibold text-emerald-800 dark:text-emerald-100 mb-2 flex items-center">
                      <Sparkles className="w-4 h-4 mr-2" />
                      Mental Health Goals
                    </h3>
                    <div className="flex flex-wrap gap-2">
                      {viewingProfile.mental_health_goals.map(goal => (
                        <span key={goal} className="px-3 py-1 bg-emerald-100 dark:bg-emerald-800 text-emerald-700 dark:text-emerald-200 text-sm rounded-full">
                          {goal}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Interests */}
                {viewingProfile.interests_hobbies && viewingProfile.interests_hobbies.length > 0 && (
                  <div className="mb-6">
                    <h3 className="text-sm font-semibold text-emerald-800 dark:text-emerald-100 mb-2 flex items-center">
                      <Heart className="w-4 h-4 mr-2" />
                      Interests & Hobbies
                    </h3>
                    <div className="flex flex-wrap gap-2">
                      {viewingProfile.interests_hobbies.map(interest => (
                        <span key={interest} className="px-3 py-1 bg-teal-100 dark:bg-teal-800 text-teal-700 dark:text-teal-200 text-sm rounded-full">
                          {interest}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Lifestyle Section */}
                <div className="mb-6">
                  <h3 className="text-sm font-semibold text-emerald-800 dark:text-emerald-100 mb-3 flex items-center">
                    <Target className="w-4 h-4 mr-2" />
                    Lifestyle & Preferences
                  </h3>
                  <div className="grid grid-cols-2 gap-3">
                    {viewingProfile.relationship_goals && (
                      <div className="p-3 bg-emerald-50 dark:bg-slate-700 rounded-lg">
                        <span className="text-xs text-emerald-600 dark:text-emerald-400 font-medium">Relationship Goals</span>
                        <p className="text-emerald-800 dark:text-emerald-100 text-sm capitalize">{viewingProfile.relationship_goals}</p>
                      </div>
                    )}
                    {viewingProfile.body_type && (
                      <div className="p-3 bg-emerald-50 dark:bg-slate-700 rounded-lg">
                        <span className="text-xs text-emerald-600 dark:text-emerald-400 font-medium">Body Type</span>
                        <p className="text-emerald-800 dark:text-emerald-100 text-sm capitalize">{viewingProfile.body_type}</p>
                      </div>
                    )}
                    {viewingProfile.alcohol && (
                      <div className="p-3 bg-emerald-50 dark:bg-slate-700 rounded-lg flex items-center space-x-2">
                        <Wine className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
                        <div>
                          <span className="text-xs text-emerald-600 dark:text-emerald-400 font-medium">Alcohol</span>
                          <p className="text-emerald-800 dark:text-emerald-100 text-sm capitalize">{viewingProfile.alcohol}</p>
                        </div>
                      </div>
                    )}
                    {viewingProfile.smoking && (
                      <div className="p-3 bg-emerald-50 dark:bg-slate-700 rounded-lg flex items-center space-x-2">
                        <Cigarette className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
                        <div>
                          <span className="text-xs text-emerald-600 dark:text-emerald-400 font-medium">Smoking</span>
                          <p className="text-emerald-800 dark:text-emerald-100 text-sm capitalize">{viewingProfile.smoking}</p>
                        </div>
                      </div>
                    )}
                    {viewingProfile.religious_beliefs && (
                      <div className="p-3 bg-emerald-50 dark:bg-slate-700 rounded-lg flex items-center space-x-2">
                        <Cross className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
                        <div>
                          <span className="text-xs text-emerald-600 dark:text-emerald-400 font-medium">Religion</span>
                          <p className="text-emerald-800 dark:text-emerald-100 text-sm capitalize">{viewingProfile.religious_beliefs}</p>
                        </div>
                      </div>
                    )}
                    {viewingProfile.therapy_experience && (
                      <div className="p-3 bg-emerald-50 dark:bg-slate-700 rounded-lg">
                        <span className="text-xs text-emerald-600 dark:text-emerald-400 font-medium">Therapy Experience</span>
                        <p className="text-emerald-800 dark:text-emerald-100 text-sm capitalize">{viewingProfile.therapy_experience}</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Looking For */}
                {viewingProfile.looking_for_description && (
                  <div className="mb-6">
                    <h3 className="text-sm font-semibold text-emerald-800 dark:text-emerald-100 mb-2">
                      What I'm Looking For
                    </h3>
                    <p className="text-emerald-700 dark:text-emerald-200 text-sm">
                      {viewingProfile.looking_for_description}
                    </p>
                  </div>
                )}
              </div>

              {/* Footer */}
              <div className="p-4 border-t border-emerald-100 dark:border-slate-600">
                <button
                  onClick={() => setViewingProfile(null)}
                  className="w-full py-3 bg-emerald-500 text-white rounded-xl font-medium hover:bg-emerald-600 transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Date Planning Assistant Modal */}
        {showDatePlanner && activeConversation && (
          <DatePlanningAssistant
            matchUserId={activeConversation.id}
            matchName={getDisplayName(activeConversation.name)}
            onClose={() => setShowDatePlanner(false)}
            isModal={true}
          />
        )}
      </div>
    </div>
  );
};

export default Messages;